/**
 * Derived from Open Science `apps/desktop/src/components/thread/ToolGroup.tsx`
 * at e2333ef9bdefb9c5438c54fc78d93fe56ec72ed0 (MIT).
 *
 * The grouping, automatic disclosure, delayed unmount, and nested detail
 * structure are retained. `ThreadBlock` is replaced with a projection of
 * Cadence's public, ProjectTurn-scoped Agent events; ProjectTurn ownership,
 * call pairing, and plan/cooperation projections remain Cadence-specific.
 */
import {
  Activity,
  Brain,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  LoaderCircle,
  Network,
  Search,
  Terminal,
  Wrench,
} from "lucide-react";
import {
  memo,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { compareOrganizationAgentEvents } from "./collaborationModel";
import type { CadenceAgent, CadenceAgentEvent } from "./types";

type ActivityStatus = "running" | "completed" | "failed" | "incomplete";
type ToolFamily = "mcp" | "web_search" | "exec";

export type ConversationActivityBlock =
  | ConversationReasoningBlock
  | ConversationToolBlock;

export interface ConversationReasoningBlock {
  kind: "reasoning";
  key: string;
  agentId: string;
  submissionId: string;
  /** Server-issued identity for one public reasoning summary stream. */
  reasoningId: string;
  text: string;
  /**
   * Only the Runtime's public reasoning lifecycle may make a thought live.
   * A ProjectTurn merely says the overall request is active; it cannot prove
   * that an earlier reasoning summary is still streaming.
   */
  streaming: boolean;
}

export interface ConversationToolBlock {
  kind: "tool-call";
  key: string;
  agentId: string;
  submissionId: string;
  family: ToolFamily;
  status: ActivityStatus;
  title: string;
  verb: string;
  command?: string;
  output?: string;
  partialOutput?: string;
  startedAt?: number;
  endedAt?: number;
}

export type ConversationActivityListItem =
  | { kind: "group"; start: number; blocks: ConversationActivityBlock[] }
  | { kind: "block"; index: number; block: ConversationActivityBlock };

interface ConversationPlan {
  key: string;
  agentId: string;
  submissionId: string;
  steps: Array<{
    step: string;
    status: "pending" | "in_progress" | "completed";
  }>;
}

interface ConversationCoordination {
  key: string;
  label: string;
  active: boolean;
}

function eventRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function eventText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function eventDelta(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function toolFamily(type: string): ToolFamily | undefined {
  if (type === "mcp_tool_call_begin" || type === "mcp_tool_call_end") {
    return "mcp";
  }
  if (type === "web_search_begin" || type === "web_search_end") {
    return "web_search";
  }
  if (
    type === "exec_command_begin" ||
    type === "exec_command_output_delta" ||
    type === "exec_command_end"
  ) {
    return "exec";
  }
  return undefined;
}

function isToolBegin(type: string) {
  return (
    type === "mcp_tool_call_begin" ||
    type === "web_search_begin" ||
    type === "exec_command_begin"
  );
}

function isToolEnd(type: string) {
  return (
    type === "mcp_tool_call_end" ||
    type === "web_search_end" ||
    type === "exec_command_end"
  );
}

function publicReasoningId(event: CadenceAgentEvent): string | undefined {
  if (event.event.visibility !== "public") return undefined;
  return eventText(event.event.reasoning_id);
}

function reasoningText(event: CadenceAgentEvent): string | undefined {
  if (
    ![
      "agent_reasoning",
      "agent_reasoning_delta",
      "reasoning_content_delta",
    ].includes(event.event.type)
  ) {
    return undefined;
  }
  // Reasoning is private by default. The server-owned projection must attest
  // both public visibility and one stable section ID; event type alone is not
  // authority to expose an old Runtime thought or merge it into a new section.
  if (!publicReasoningId(event)) return undefined;
  const value = event.event.text ?? event.event.delta;
  return (isReasoningDelta(event.event.type)
    ? eventDelta(value)
    : eventText(value)) || undefined;
}

function isReasoningDelta(type: string) {
  return type === "agent_reasoning_delta" || type === "reasoning_content_delta";
}

function reasoningId(event: CadenceAgentEvent): string {
  // `reasoningText` has already required this server-issued ID. Keeping the
  // fallback absent prevents a future caller from recreating the ambiguous
  // `runtime-summary` bucket for legacy events.
  return publicReasoningId(event) ?? "unreachable-public-reasoning-id";
}

function reasoningStreamState(
  event: CadenceAgentEvent,
): "streaming" | "terminal" | undefined {
  // Older durable rollout records remain readable, but intentionally do not
  // gain a fabricated live state after reload. New live relays explicitly
  // attest both visibility and lifecycle before the UI auto-discloses them.
  if (event.event.visibility !== "public") return undefined;
  const state = eventText(event.event.stream_state).toLowerCase();
  if (state === "started" || state === "delta") return "streaming";
  if (state === "completed" || state === "aborted") return "terminal";
  return undefined;
}

function reasoningScope(event: CadenceAgentEvent) {
  return `${event.agentId}:${event.submissionId}:${reasoningId(event)}`;
}

function formatCommand(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    const command = value.map(eventText).filter(Boolean).join(" ");
    return command || undefined;
  }
  return eventText(value) || undefined;
}

function durationTimestamp(event: CadenceAgentEvent) {
  return typeof event.tsMs === "number" && Number.isFinite(event.tsMs)
    ? event.tsMs
    : undefined;
}

function activityKey(
  projectId: string,
  event: CadenceAgentEvent,
  family: ToolFamily,
  callId: string,
) {
  return [projectId, event.agentId, event.submissionId, family, callId].join(
    ":",
  );
}

function toolLabel(event: CadenceAgentEvent, family: ToolFamily): string {
  if (family === "exec") {
    return formatCommand(event.event.command) ?? "运行命令";
  }
  if (family === "web_search") {
    return eventText(event.event.query) || "检索文献";
  }
  const invocation = eventRecord(event.event.invocation);
  const tool = eventText(invocation?.tool);
  const server = eventText(invocation?.server);
  if (tool && server) return `${tool} · ${server}`;
  return tool || "调用科研工具";
}

function toolVerb(family: ToolFamily): string {
  return {
    mcp: "调用",
    web_search: "检索",
    exec: "运行",
  }[family];
}

function publicOutput(value: unknown): string | undefined {
  if (typeof value === "string") return value || undefined;
  if (value === undefined || value === null) return undefined;
  try {
    const rendered = JSON.stringify(value, null, 2);
    return rendered === "{}" || rendered === "[]" ? undefined : rendered;
  } catch {
    return undefined;
  }
}

function outputForToolEnd(
  event: CadenceAgentEvent,
  family: ToolFamily,
): string | undefined {
  if (family === "exec") {
    return (
      eventText(event.event.formatted_output) ||
      eventText(event.event.aggregated_output) ||
      [eventText(event.event.stdout), eventText(event.event.stderr)]
        .filter(Boolean)
        .join("\n") ||
      undefined
    );
  }
  if (family === "mcp") {
    return publicOutput(event.event.result ?? event.event.structured_content);
  }
  return eventText(event.event.query) || undefined;
}

function toolFailed(event: CadenceAgentEvent, family: ToolFamily): boolean {
  if (event.event.success === false) return true;
  if (family === "exec") {
    const status = eventText(event.event.status).toLowerCase();
    return status === "failed" || status === "declined";
  }
  if (family === "mcp") {
    const result = eventRecord(event.event.result);
    if (result && "Err" in result) return true;
    const ok = eventRecord(result?.Ok);
    return ok?.isError === true;
  }
  return false;
}

function appendOutput(current: string | undefined, delta: string): string | undefined {
  if (!delta) return current;
  return `${current ?? ""}${delta}`;
}

/**
 * ProjectTurn activities can contain interleaved Agents. Unlike Open Science's
 * single-thread block list, a group must not merge different Agent submissions.
 */
export function groupConversationActivityBlocks(
  blocks: ConversationActivityBlock[],
): ConversationActivityListItem[] {
  const items: ConversationActivityListItem[] = [];
  let group:
    | { start: number; blocks: ConversationActivityBlock[]; scope: string }
    | undefined;
  const flush = () => {
    const current = group;
    group = undefined;
    if (!current) return;
    if (current.blocks.some((block) => block.kind === "tool-call")) {
      items.push({
        kind: "group",
        start: current.start,
        blocks: current.blocks,
      });
    } else {
      current.blocks.forEach((block, index) =>
        items.push({ kind: "block", index: current.start + index, block }),
      );
    }
  };

  blocks.forEach((block, index) => {
    const groupable = block.kind === "tool-call" || block.kind === "reasoning";
    const scope = `${block.agentId}:${block.submissionId}`;
    if (groupable) {
      if (group && group.scope !== scope) flush();
      group ??= { start: index, blocks: [], scope };
      group.blocks.push(block);
      return;
    }
    flush();
    items.push({ kind: "block", index, block });
  });
  flush();
  return items;
}

/** One phrase per real operation family, preserving first-seen order. */
export function summarizeConversationActivityGroup(
  blocks: ConversationActivityBlock[],
): string {
  const counts = new Map<string, number>();
  blocks.forEach((block) => {
    if (block.kind !== "tool-call") return;
    counts.set(block.verb, (counts.get(block.verb) ?? 0) + 1);
  });
  return [...counts.entries()]
    .map(([verb, count]) => {
      if (verb === "运行") return `运行 ${count} 个命令`;
      if (verb === "检索") return `检索 ${count} 次`;
      return `调用 ${count} 个工具`;
    })
    .join("，");
}

/**
 * Builds a stable UI projection from public runtime events. An end or output
 * event without its real begin/call_id is intentionally omitted rather than
 * guessed into a nearby command.
 */
export function conversationActivityBlocksFromEvents(
  projectId: string,
  events: CadenceAgentEvent[],
  running: boolean,
): ConversationActivityBlock[] {
  const blocks: ConversationActivityBlock[] = [];
  const toolIndexByKey = new Map<string, number>();
  const reasoningIndexByScope = new Map<string, number>();
  const detailedReasoningDeltas = new Map<string, Set<string>>();
  const sorted = events
    .filter((event) => !event.projectId || event.projectId === projectId)
    .slice()
    .sort(compareOrganizationAgentEvents);

  sorted.forEach((event) => {
    const reasoning = reasoningText(event);
    if (reasoning) {
      const scope = reasoningScope(event);
      const deltaScope = `${event.agentId}:${event.submissionId}`;
      const isDetailedDelta = event.event.type === "reasoning_content_delta";
      const genericDuplicate =
        event.event.type === "agent_reasoning_delta" &&
        detailedReasoningDeltas.get(deltaScope)?.has(reasoning);
      if (isDetailedDelta) {
        const seen = detailedReasoningDeltas.get(deltaScope) ?? new Set<string>();
        seen.add(reasoning);
        detailedReasoningDeltas.set(deltaScope, seen);
      }
      if (genericDuplicate) return;

      const existingIndex = reasoningIndexByScope.get(scope);
      const existing =
        existingIndex === undefined ? undefined : blocks[existingIndex];
      if (existing?.kind === "reasoning") {
        if (isReasoningDelta(event.event.type)) {
          existing.text += reasoning;
        } else {
          // A final public summary is authoritative for this exact summary
          // stream. Replace accumulated transport deltas rather than showing
          // both provider spellings of the same thought.
          existing.text = reasoning;
        }
        existing.streaming = reasoningStreamState(event) === "streaming";
      } else {
        reasoningIndexByScope.set(scope, blocks.length);
        blocks.push({
          kind: "reasoning",
          key: event.key,
          agentId: event.agentId,
          submissionId: event.submissionId,
          reasoningId: reasoningId(event),
          text: reasoning,
          streaming: reasoningStreamState(event) === "streaming",
        });
      }
      return;
    }

    const family = toolFamily(event.event.type);
    const callId = eventText(event.event.call_id);
    if (!family || !callId) return;
    const key = activityKey(projectId, event, family, callId);
    const existingIndex = toolIndexByKey.get(key);

    if (isToolBegin(event.event.type)) {
      if (existingIndex !== undefined) return;
      toolIndexByKey.set(key, blocks.length);
      blocks.push({
        kind: "tool-call",
        key,
        agentId: event.agentId,
        submissionId: event.submissionId,
        family,
        status: "running",
        title: toolLabel(event, family),
        verb: toolVerb(family),
        command: formatCommand(event.event.command),
        startedAt: durationTimestamp(event),
      });
      return;
    }

    if (existingIndex === undefined) return;
    const existing = blocks[existingIndex];
    if (existing?.kind !== "tool-call") return;

    if (event.event.type === "exec_command_output_delta") {
      existing.partialOutput = appendOutput(
        existing.partialOutput,
        eventDelta(event.event.delta ?? event.event.chunk),
      );
      return;
    }

    if (!isToolEnd(event.event.type)) return;
    existing.status = toolFailed(event, family) ? "failed" : "completed";
    existing.title = toolLabel(event, family) || existing.title;
    existing.command = formatCommand(event.event.command) ?? existing.command;
    existing.output = outputForToolEnd(event, family) ?? existing.partialOutput;
    existing.endedAt = durationTimestamp(event);
  });

  if (!running) {
    blocks.forEach((block) => {
      if (block.kind === "reasoning") {
        block.streaming = false;
        return;
      }
      if (block.kind !== "tool-call" || block.status !== "running") return;
      // A settled ProjectTurn proves only that this execution record did not
      // close in the public history. It is not evidence that the tool is live.
      block.status = "incomplete";
      block.output ??= block.partialOutput;
    });
  }
  return blocks;
}

function formatElapsed(milliseconds: number): string {
  const seconds = Math.max(0, Math.floor(milliseconds / 1_000));
  if (seconds < 60) return `${seconds} 秒`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes} 分 ${rest} 秒` : `${minutes} 分`;
}

function Elapsed({ start }: { start: number }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setTick((current) => current + 1), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  return <span className="conversation-activity-elapsed">{formatElapsed(Date.now() - start)}</span>;
}

/**
 * Directly adapted from Open Science's Collapse: closed execution detail is
 * removed after the transition rather than retained for every historical turn.
 */
function Collapse({ open, children }: { open: boolean; children: ReactNode }) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
      const frame = window.requestAnimationFrame(() => setShown(true));
      return () => window.cancelAnimationFrame(frame);
    }
    setShown(false);
    const timer = window.setTimeout(() => setMounted(false), 300);
    return () => window.clearTimeout(timer);
  }, [open]);
  if (!mounted) return null;
  return (
    <div
      className={`conversation-activity-collapse ${shown ? "is-shown" : ""}`}
    >
      <div>{children}</div>
    </div>
  );
}

function ToolDetail({ block }: { block: ConversationToolBlock }) {
  if (!block.command && !block.output) return null;
  return (
    <div className="conversation-activity-detail">
      {block.command && <pre>{`$ ${block.command}`}</pre>}
      {block.output && <pre>{block.output}</pre>}
    </div>
  );
}

function ToolStatusIcon({ status }: { status: ActivityStatus }) {
  if (status === "running") {
    return <LoaderCircle className="conversation-activity-spinner" size={13} aria-label="执行中" />;
  }
  if (status === "incomplete") {
    return <CircleAlert className="conversation-activity-incomplete" size={13} aria-label="执行记录未确认" />;
  }
  if (status === "failed") {
    return <CircleAlert className="conversation-activity-failed" size={13} aria-label="执行失败" />;
  }
  return <CheckCircle2 className="conversation-activity-completed" size={13} aria-label="已完成" />;
}

function ToolFamilyIcon({ family }: { family: ToolFamily }) {
  if (family === "exec") return <Terminal size={13} aria-hidden="true" />;
  if (family === "web_search") return <Search size={13} aria-hidden="true" />;
  return <Wrench size={13} aria-hidden="true" />;
}

// Retains Open Science's nested row disclosure: group first, then one tool.
const ToolRow = memo(function ToolRow({ block }: { block: ConversationToolBlock }) {
  const running = block.status === "running";
  const detail = running ? null : <ToolDetail block={block} />;
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const open = (userOpen ?? false) && Boolean(detail);
  const duration =
    block.startedAt !== undefined && block.endedAt !== undefined
      ? Math.max(0, block.endedAt - block.startedAt)
      : undefined;
  const rowContent = (
    <>
      <ToolStatusIcon status={block.status} />
      <span
        className="conversation-activity-family"
        data-family={block.family}
        aria-label={
          block.family === "exec"
            ? "命令执行"
            : block.family === "web_search"
              ? "外部检索"
              : "科研工具"
        }
      >
        <ToolFamilyIcon family={block.family} />
      </span>
      <span className="conversation-activity-verb">{block.verb}</span>
      <span
        className="conversation-activity-title"
        title={block.command ?? block.title}
      >
        {block.title}
      </span>
      {block.status === "incomplete" && (
        <span className="conversation-activity-incomplete-label">记录未确认</span>
      )}
      {detail && (
        <ChevronRight
          className={open ? "is-open" : ""}
          size={13}
          aria-hidden="true"
        />
      )}
      {running && block.startedAt !== undefined && <Elapsed start={block.startedAt} />}
      {duration !== undefined && duration >= 1_000 && (
        <span className="conversation-activity-elapsed">{formatElapsed(duration)}</span>
      )}
    </>
  );
  return (
    <div className="conversation-activity-tool" data-status={block.status}>
      {detail ? (
        <button
          className="conversation-activity-tool-row is-expandable"
          data-testid="conversation-activity-tool-row"
          type="button"
          aria-expanded={open}
          aria-label={`${open ? "收起" : "展开"}${block.title}的执行详情`}
          onClick={() => setUserOpen(!open)}
        >
          {rowContent}
        </button>
      ) : (
        <div
          className="conversation-activity-tool-row"
          data-testid="conversation-activity-tool-row"
        >
          {rowContent}
        </div>
      )}
      {running && block.partialOutput && (
        <pre className="conversation-activity-live-tail">
          {block.partialOutput.replace(/\s+$/u, "").split("\n").slice(-8).join("\n")}
        </pre>
      )}
      {detail && <Collapse open={open}>{detail}</Collapse>}
    </div>
  );
});

// Directly follows Open Science's reasoning disclosure: the public thought is
// live only with submission-level authority, then folds until the user asks.
function ReasoningRow({
  block,
  inline = false,
}: {
  block: ConversationReasoningBlock;
  inline?: boolean;
}) {
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const text = block.text.trim();
  if (!text) return null;
  const open = userOpen ?? Boolean(block.streaming);
  return (
    <div
      className={`conversation-activity-reasoning ${inline ? "is-inline" : ""}`}
      data-streaming={block.streaming || undefined}
    >
      <button
        className="conversation-activity-reasoning-toggle"
        data-testid="conversation-activity-reasoning-toggle"
        type="button"
        aria-expanded={open}
        onClick={() => setUserOpen(!open)}
      >
        {block.streaming ? (
          <LoaderCircle className="conversation-activity-spinner" size={12} aria-label="正在思考" />
        ) : (
          <Brain size={13} aria-hidden="true" />
        )}
        <span>{block.streaming ? "正在思考" : "思考摘要"}</span>
        {!open && <small>展开查看公开摘要</small>}
        <ChevronRight className={open ? "is-open" : ""} size={13} aria-hidden="true" />
      </button>
      <Collapse open={open}>
        <div className="conversation-activity-reasoning-copy">
          <p>{text}</p>
        </div>
      </Collapse>
    </div>
  );
}

function ToolGroup({ blocks }: { blocks: ConversationActivityBlock[] }) {
  const tools = blocks.filter(
    (block): block is ConversationToolBlock => block.kind === "tool-call",
  );
  const active =
    tools.some((block) => block.status === "running") ||
    blocks.some((block) => block.kind === "reasoning" && block.streaming);
  const failed = tools.filter((block) => block.status === "failed").length;
  const [autoOpen, setAutoOpen] = useState(active);
  useEffect(() => {
    if (active) {
      setAutoOpen(true);
      return;
    }
    const timer = window.setTimeout(() => setAutoOpen(false), 2_000);
    return () => window.clearTimeout(timer);
  }, [active]);
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const open = userOpen ?? autoOpen;
  const rows = blocks.map((block) =>
    block.kind === "reasoning" ? (
      <ReasoningRow block={block} inline key={block.key} />
    ) : (
      <ToolRow block={block} key={block.key} />
    ),
  );

  if (blocks.length === 1 && blocks[0].kind === "tool-call") {
    return <div>{rows}</div>;
  }
  return (
    <div className="conversation-activity-group">
      <button
        className="conversation-activity-group-toggle"
        data-testid="conversation-activity-group"
        type="button"
        aria-expanded={open}
        onClick={() => setUserOpen(!open)}
      >
        {active ? (
          <LoaderCircle className="conversation-activity-spinner" size={13} aria-label="执行中" />
        ) : failed > 0 ? (
          <CircleAlert className="conversation-activity-failed" size={13} aria-label="存在失败的执行" />
        ) : (
          <CheckCircle2 className="conversation-activity-group-completed" size={13} aria-hidden="true" />
        )}
        <span>{summarizeConversationActivityGroup(blocks)}</span>
        <small>{tools.length} 项</small>
        <ChevronRight className={open ? "is-open" : ""} size={13} aria-hidden="true" />
        {failed > 0 && <strong>{failed} 项失败</strong>}
      </button>
      <Collapse open={open}>
        <div className="conversation-activity-group-rows">{rows}</div>
      </Collapse>
    </div>
  );
}

function planSteps(event: CadenceAgentEvent): ConversationPlan["steps"] {
  const structuredDelta = eventRecord(event.event.delta);
  const plan = Array.isArray(event.event.plan)
    ? event.event.plan
    : Array.isArray(structuredDelta?.plan)
      ? structuredDelta.plan
      : undefined;
  if (!plan) return [];
  const steps: ConversationPlan["steps"] = [];
  plan.forEach((item) => {
    const record = eventRecord(item);
    const step = eventText(record?.step);
    const status = eventText(record?.status).toLowerCase();
    if (!step) return;
    if (status === "completed") {
      steps.push({ step, status: "completed" });
      return;
    }
    if (status === "in_progress" || status === "in-progress") {
      steps.push({ step, status: "in_progress" });
      return;
    }
    if (status === "pending") steps.push({ step, status: "pending" });
  });
  return steps;
}

function plansFromEvents(events: CadenceAgentEvent[]): ConversationPlan[] {
  const latest = new Map<string, ConversationPlan>();
  events
    .filter(
      (event) =>
        event.event.type === "plan_update" || event.event.type === "plan_delta",
    )
    .slice()
    .sort(compareOrganizationAgentEvents)
    .forEach((event) => {
      const steps = planSteps(event);
      if (!steps.length) return;
      const key = `${event.agentId}:${event.submissionId}`;
      latest.set(key, {
        key,
        agentId: event.agentId,
        submissionId: event.submissionId,
        steps,
      });
    });
  return [...latest.values()];
}

// Adapted from Open Science's StepSummaryRow: concise until a user asks for steps.
function PlanSummaryRow({
  plan,
  agentName,
}: {
  plan: ConversationPlan;
  agentName: string;
}) {
  const [open, setOpen] = useState(false);
  const completed = plan.steps.filter((step) => step.status === "completed").length;
  return (
    <div className="conversation-plan-summary">
      <button
        type="button"
        data-testid="conversation-plan-summary"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <ChevronRight className={open ? "is-open" : ""} size={13} aria-hidden="true" />
        <span>{agentName} 的研究计划</span>
        <small>{completed} / {plan.steps.length}</small>
      </button>
      <Collapse open={open}>
        <ol>
          {plan.steps.map((step) => (
            <li data-status={step.status} key={step.step}>
              <span />
              {step.step}
            </li>
          ))}
        </ol>
      </Collapse>
    </div>
  );
}

function coordinationFromEvents(
  projectId: string,
  events: CadenceAgentEvent[],
): ConversationCoordination[] {
  const latest = new Map<string, ConversationCoordination>();
  events
    .filter((event) => event.projectId === undefined || event.projectId === projectId)
    .slice()
    .sort(compareOrganizationAgentEvents)
    .forEach((event) => {
      const type = event.event.type;
      const match = /^(collab_(?:agent_spawn|agent_interaction|waiting))_(begin|end)$/u.exec(type);
      const callId = eventText(event.event.call_id);
      if (!match || !callId) return;
      const category = match[1];
      const key = [projectId, event.agentId, event.submissionId, category, callId].join(":");
      const label = {
        collab_agent_spawn: "协作者启动",
        collab_agent_interaction: "Agent 协作",
        collab_waiting: "等待协作者",
      }[category] ?? "Agent 协作";
      latest.set(key, { key, label, active: match[2] === "begin" });
    });
  return [...latest.values()].slice(-3);
}

export function ConversationActivityFeed({
  projectId,
  events,
  agents,
  running,
}: {
  projectId: string;
  events: CadenceAgentEvent[];
  agents: CadenceAgent[];
  running: boolean;
}) {
  const blocks = useMemo(
    () => conversationActivityBlocksFromEvents(projectId, events, running),
    [events, projectId, running],
  );
  const items = useMemo(() => groupConversationActivityBlocks(blocks), [blocks]);
  const plans = useMemo(() => plansFromEvents(events), [events]);
  const coordination = useMemo(
    () => coordinationFromEvents(projectId, events),
    [events, projectId],
  );
  const agentNames = useMemo(
    () => new Map(agents.map((agent) => [agent.id, agent.name])),
    [agents],
  );
  const activityRunning = blocks.some(
    (block) =>
      (block.kind === "reasoning" && block.streaming) ||
      (block.kind === "tool-call" && block.status === "running"),
  );

  if (!items.length && !plans.length && !coordination.length) return null;
  return (
    <section
      className="conversation-activity-feed"
      data-testid="conversation-activity-feed"
      aria-label="当前请求的执行活动"
    >
      <header>
        <span className="conversation-activity-heading">
          {activityRunning ? (
            <LoaderCircle className="conversation-activity-spinner" size={13} aria-hidden="true" />
          ) : (
            <Activity size={13} aria-hidden="true" />
          )}
          执行轨迹
        </span>
        <small>
          {activityRunning
            ? "正在执行"
            : blocks.length > 0
              ? `${blocks.filter((block) => block.kind === "tool-call").length} 项工具`
              : "已记录"}
        </small>
      </header>
      {plans.map((plan) => (
        <PlanSummaryRow
          agentName={agentNames.get(plan.agentId) ?? plan.agentId}
          key={plan.key}
          plan={plan}
        />
      ))}
      {coordination.length > 0 && (
        <div className="conversation-coordination-strip" data-testid="conversation-coordination-strip">
          <Network size={13} aria-hidden="true" />
          {coordination.map((item) => (
            <span data-active={item.active || undefined} key={item.key}>
              {item.active && <LoaderCircle className="conversation-activity-spinner" size={11} aria-hidden="true" />}
              {item.label}
            </span>
          ))}
        </div>
      )}
      {items.length > 0 && (
        <div className="conversation-activity-list">
          {items.map((item) =>
            item.kind === "group" ? (
              <ToolGroup blocks={item.blocks} key={`${item.start}:${item.blocks[0]?.key ?? "group"}`} />
            ) : item.block.kind === "reasoning" ? (
              <ReasoningRow block={item.block} key={item.block.key} />
            ) : (
              <ToolRow block={item.block} key={item.block.key} />
            ),
          )}
        </div>
      )}
    </section>
  );
}
